import { useSearchParams } from "react-router-dom";
import type { Section, SloSummary, SloUnknownReason } from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { num, pct } from "@/lib/format";

/**
 * HTTP 요청 SLO · Error Budget 카드 — ADR 0051 · Issue #157
 * --------------------------------------------------------------------------
 * **조회 전용**입니다. 값은 `useWorkloadDetail`이 이미 받아 온 같은 응답의
 * `slo` 섹션에서 옵니다 — 새 요청도, 새 훅도, 폴링도 만들지 않습니다.
 *
 * - 목표 선택기는 **서버가 준 `applicableObjectives`만** 씁니다. 브라우저가 id를
 *   지어내지 않고, 목록 밖 id를 보내면 서버가 `objective_unknown`으로 닫습니다.
 * - 선택은 URL `?objective=`에 둡니다. 자동 갱신이 선택을 지우지 않습니다.
 * - 화면의 시간 범위는 SLO 창을 **바꾸지 않습니다**. 창은 목표가 정합니다.
 * - 상태 색은 예약어라 언제나 아이콘 + 텍스트 라벨과 함께 씁니다(StatusBadge).
 *   원시 hex·임의 픽셀값을 쓰지 않고 역할 토큰 클래스만 참조합니다.
 * - "데이터 없음 · 관측 부족 · 신선도 부족 · 상류 장애"를 같은 빈 화면으로
 *   처리하지 않습니다.
 */

/**
 * 미상 사유 → 문구 id.
 *
 * 닫힌 집합을 **운영자의 행동**으로 묶습니다. 내부 코드 하나하나를 문장으로 옮기면
 * 화면이 구현 내부로 채워지고, 정작 무엇을 해야 하는지가 흐려집니다. 대신 서로 다른
 * 행동을 요구하는 사유는 **반드시 다른 문장**을 갖습니다.
 *
 * 템플릿으로 id를 조립하지 않습니다 — 조립하면 카탈로그 게이트가 "쓰이지 않는
 * 문구"로 읽고, 사유가 늘어도 아무도 모르게 원문 코드가 화면에 그대로 나갑니다.
 * 이 표는 계약의 union을 전부 덮어야 타입 검사를 통과합니다.
 */
const REASON_MESSAGE: Record<SloUnknownReason, string> = {
  slo_not_configured: "common.slo.reason.notConfigured",
  objective_unknown: "common.slo.reason.objectiveUnknown",
  objective_not_applicable: "common.slo.reason.objectiveUnknown",
  kind_unsupported: "common.slo.reason.kindUnsupported",
  workload_uid_missing: "common.slo.reason.scopeUnsupported",
  scope_unsupported: "common.slo.reason.scopeUnsupported",
  source_not_configured: "common.slo.reason.sourceUnverified",
  source_provenance_unverified: "common.slo.reason.sourceUnverified",
  source_ingestion_unqualified: "common.slo.reason.ingestionUnqualified",
  window_unsupported: "common.slo.reason.windowUnsupported",
  window_clamped_too_short: "common.slo.reason.windowUnsupported",
  query_unsupported: "common.slo.reason.queryUnsupported",
  no_data: "common.slo.reason.noData",
  no_eligible_events: "common.slo.reason.lowTraffic",
  low_traffic: "common.slo.reason.lowTraffic",
  insufficient_coverage: "common.slo.reason.insufficientCoverage",
  stale_data: "common.slo.reason.staleData",
  invalid_status_share: "common.slo.reason.invalidStatusShare",
  invalid_sample: "common.slo.reason.invalidResponse",
  invalid_ratio: "common.slo.reason.invalidResponse",
  invalid_response: "common.slo.reason.invalidResponse",
  scope_mismatch: "common.slo.reason.invalidResponse",
  cardinality_exceeded: "common.slo.reason.tooLarge",
  result_too_large: "common.slo.reason.tooLarge",
  upstream_unavailable: "common.slo.reason.upstream",
  timeout: "common.slo.reason.upstream",
  circuit_open: "common.slo.reason.upstream",
};

/**
 * 상류 장애로 **볼 수 있는** 사유입니다.
 *
 * 나머지는 "관측이 없다 · 기능이 꺼져 있다 · 표본이 모자라다" 같은 **사실**이지
 * 장애가 아닙니다. 그것까지 주황색 `일부 장애` 배지로 칠하면 운영자가 멀쩡한
 * 데이터소스를 의심합니다. 이 구분은 사유 코드를 아는 이 카드만 할 수 있습니다.
 */
const OUTAGE_REASONS: ReadonlySet<SloUnknownReason> = new Set([
  "upstream_unavailable",
  "timeout",
  "circuit_open",
  "invalid_response",
  "scope_mismatch",
  "invalid_sample",
  "invalid_ratio",
  "result_too_large",
  "cardinality_exceeded",
]);

/**
 * 초 단위 창을 언어 중립 라벨로 바꿉니다.
 *
 * 등록된 SLO 창 이름(`1h`·`6h`·`24h`·`7d`·`30d`)과 **같은 표기**여야 합니다 —
 * 같은 값을 창 이름은 `24h`, 상세는 `1d`로 적으면 서로 다른 창으로 읽힙니다.
 */
function windowLabel(seconds: number): string {
  if (seconds <= 0) return "0s";
  if (seconds >= 604_800 && seconds % 86_400 === 0) return `${seconds / 86_400}d`;
  if (seconds % 3_600 === 0) return `${seconds / 3_600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
}

/**
 * 표시용 반올림입니다. 판정은 서버가 float64 전정밀도로 이미 끝냈습니다.
 *
 * 목표는 0.999999까지 등록할 수 있습니다. 소수 둘로만 적으면 99.9999% 목표가
 * `100.00%`로 보이고, **미달한 달성률도** `100.00%`가 되어 왜 critical인지가
 * 화면에서 사라집니다. 그래서 100%에 가까울수록 자리를 늘립니다 — 값을 바꾸는 것이
 * 아니라 **보이는 자리수**만 늘립니다.
 */
const MAX_RATIO_DIGITS = 6;

/** 100%가 아닌 값이 `100.00%`로 보이지 않을 만큼의 최소 자리수입니다. */
function distinctFromWhole(v: number, digits: number): boolean {
  return v >= 1 || pct(v * 100, digits) !== pct(100, digits);
}

function ratio(v: number, digits = 2): string {
  /* 보통은 소수 둘입니다 — 자리수를 늘리는 것은 **필요할 때만**입니다. */
  for (let d = digits; d < MAX_RATIO_DIGITS; d++) {
    if (distinctFromWhole(v, d)) return pct(v * 100, d);
  }
  return pct(v * 100, MAX_RATIO_DIGITS);
}

/**
 * 달성률과 목표는 **같은 자리수**로, 그리고 서로 구분되게 적습니다.
 *
 * 목표는 0.999999까지 등록할 수 있습니다. 두 값을 각자 줄이면 미달한 달성률이
 * 목표와 똑같은 문자열로 보여, 왜 목표 미달인지가 화면에서 사라집니다.
 */
function ratioPair(sli: number, target: number): { sli: string; target: string } {
  for (let d = 2; d < MAX_RATIO_DIGITS; d++) {
    const a = pct(sli * 100, d);
    const b = pct(target * 100, d);
    if (distinctFromWhole(sli, d) && distinctFromWhole(target, d) && (sli === target || a !== b)) {
      return { sli: a, target: b };
    }
  }
  return { sli: pct(sli * 100, MAX_RATIO_DIGITS), target: pct(target * 100, MAX_RATIO_DIGITS) };
}

export function SloCard({ section, referenceIso }: { section?: Section<SloSummary>; referenceIso?: string }) {
  const { t } = useT("common");
  const [params, setParams] = useSearchParams();
  const data = section?.data;
  const reason = section?.status === "degraded" ? (section.reason as SloUnknownReason) : undefined;
  const options = data?.applicableObjectives ?? [];
  const result = data?.result;
  const population = data?.population;
  const window = data?.window;

  const outage = reason !== undefined && OUTAGE_REASONS.has(reason);
  /* 상태는 색만으로 전달하지 않습니다 — 아이콘 + 텍스트 라벨이 항상 함께 갑니다. */
  const severity = !result ? "unknown" : !result.met ? "critical" : result.burnRate > 1 ? "warning" : "healthy";
  const stateLabel = !result
    ? t("common.slo.unknownTitle")
    : !result.met
      ? t("common.slo.missed")
      : result.burnRate > 1
        ? t("common.slo.burning")
        : t("common.slo.met");

  /** 선택은 URL에 둡니다. 빈 값은 키 자체를 지워 "서버 기본값"과 같은 요청이 됩니다. */
  const choose = (key: "objective" | "sloWindow", value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    /* 목표를 바꾸면 그 목표가 허용하지 않는 기간이 남을 수 있습니다 — 남겨 두면
       서버가 곧바로 미상으로 닫습니다. 기간 선택을 지워 서버 기본값으로 돌립니다. */
    if (key === "objective") next.delete("sloWindow");
    setParams(next, { replace: true });
  };

  /** 이 목표가 실제로 허용하는 기간입니다. 브라우저가 값을 지어내지 않습니다. */
  const windows = options.find((o) => o.id === data?.objectiveId)?.windows ?? [];

  return (
    <Panel
      title={t("common.slo.title")}
      section={section}
      referenceIso={referenceIso}
      className="slo-panel"
      outage={outage}
      actions={
        options.length > 0 ? (
          <>
            <label className="field" data-testid="slo-objective-field">
              <span className="field__label">{t("common.slo.objective")}</span>
              <select
                data-testid="slo-objective"
                value={data?.objectiveId ?? params.get("objective") ?? ""}
                onChange={(e) => choose("objective", e.target.value)}
              >
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            {windows.length > 1 && (
              <label className="field" data-testid="slo-window-field">
                <span className="field__label">{t("common.slo.period")}</span>
                <select
                  data-testid="slo-window-select"
                  value={data?.windowName ?? params.get("sloWindow") ?? ""}
                  onChange={(e) => choose("sloWindow", e.target.value)}
                >
                  {windows.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        ) : undefined
      }
    >
      <div className="grid" data-testid="slo-card">
        <div className="row">
          <StatusBadge severity={severity} label={stateLabel} />
          {data?.windowName && (
            <span className="muted" data-testid="slo-window">
              {t("common.slo.periodNote")}
            </span>
          )}
        </div>

        {result && population && (
          <>
            <div className="grid grid--kpi">
              <StatTile
                label={t("common.slo.sli")}
                value={ratioPair(result.sli, result.target).sli}
                tone={severity === "critical" ? "critical" : severity === "warning" ? "warning" : undefined}
                footnote={t("common.slo.target", { target: ratioPair(result.sli, result.target).target })}
              />
              <StatTile
                label={t("common.slo.budgetRemaining")}
                value={num(result.remainingEvents)}
                tone={result.remainingEvents <= 0 ? "critical" : undefined}
                footnote={t("common.slo.budgetConsumed", { consumed: ratio(result.consumedFraction) })}
              />
              <StatTile
                label={t("common.slo.burnRate")}
                value={result.burnRate.toFixed(2)}
                tone={result.burnRate > 1 ? "warning" : undefined}
              />
            </div>

            <div className="facts">
              <div className="fact">
                <span className="fact__label">{t("common.slo.populationLabel")}</span>
                <span className="fact__value" data-testid="slo-population">
                  {t("common.slo.population", {
                    series: num(population.observedSeries),
                    pods: num(population.observedPods),
                    minCoverage: ratio(population.minCoverage),
                    maxCoverage: ratio(population.maxCoverage),
                    emptyBuckets: num(population.emptyBuckets),
                    expectedSamples: num(population.expectedSamples),
                  })}
                </span>
              </div>
              <div className="fact">
                <span className="fact__label">{t("common.slo.eventsLabel")}</span>
                <span className="fact__value num" data-testid="slo-events">
                  {t("common.slo.events", {
                    eligible: num(population.eligibleEvents),
                    good: num(population.goodEvents),
                    excluded: num(population.excludedEvents),
                  })}
                </span>
              </div>
            </div>
            {/* 커버리지 규칙·추정치·결측 해상도를 숨기지 않습니다. */}
            <p className="muted" data-testid="slo-limits">
              {t("common.slo.limits")}
            </p>
          </>
        )}

        {window && (
          <p className="muted" data-testid="slo-window-detail">
            {t("common.slo.windowDetail", {
              effective: windowLabel(window.effectiveSeconds),
              bucket: windowLabel(window.bucketSeconds),
              count: String(window.bucketCount),
              dropped: windowLabel(window.droppedTailSeconds),
            })}
            {window.clamped && ` · ${t("common.slo.windowClamped", { requested: windowLabel(window.requestedSeconds) })}`}
          </p>
        )}

        {reason && (
          /* 장애가 아닌 미상은 **중립**으로 보여 줍니다. 상태 색은 예약어라 장애일
             때만 씁니다 — 관측이 없다는 사실을 장애처럼 칠하지 않습니다. */
          <div
            className={`state${outage ? " state--degraded" : ""}`}
            data-testid="slo-unknown"
            data-outage={outage ? "true" : "false"}
          >
            <span className="state__glyph" aria-hidden="true">
              {outage ? "!" : "·"}
            </span>
            <span className="state__title">{t("common.slo.unknownTitle")}</span>
            <span className="state__detail" data-testid="slo-reason">
              {t(REASON_MESSAGE[reason])}
            </span>
            {!outage && (
              <span className="muted" data-testid="slo-not-outage">
                {t("common.slo.notAnOutage")}
              </span>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
